import React, { useState, useEffect, useCallback, useRef } from 'react';
import en from '../../../translations/en.json';
import LabelAvatarStack from '../../../components/shared/LabelAvatarStack';
import { apiClient } from '../../../api/apiClient';
import { GET_ALBUM_SONGS_URL } from '../../../api/album';
import EmptyState from '../../../components/shared/EmptyState';
import Loader from '../../../components/shared/Loader';
import SharedHeader from '../../../components/shared/SharedHeader';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import { toast } from '../../../components/shared/toast';

function SongsTab({ album, onSelectSong }) {
  const [songs, setSongs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const tableContainerRef = useRef(null);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const fetchSongs = useCallback(async (pageNum, isAppend = false) => {
    if (!album || !album.id) return;
    if (isAppend) setLoadingMore(true);
    else setIsLoading(true);

    try {
      const res = await apiClient(`${GET_ALBUM_SONGS_URL(album.id)}?page=${pageNum}&limit=20`);
      if (res && res.songs) {
        setSongs(prev => isAppend ? [...prev, ...res.songs] : res.songs);
        setTotalCount(res.totalCount || res.songCount || 0);
        setHasMore(res.hasMore !== undefined ? res.hasMore : res.songs.length === 20);
      }
    } catch (err) {
      console.error('Failed to fetch album songs:', err);
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  }, [album?.id]);

  useEffect(() => {
    setPage(1);
    fetchSongs(1, false);
  }, [album?.id]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !loadingMore && !isLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchSongs(nextPage, true);
    }
  };

  const handleExportSongs = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      const response = await apiClient(`${GET_ALBUM_SONGS_URL(album.id)}?export=true`);
      if (response && response.songs) {
        const headers = ['Song Name', 'Sinhala Name', 'Artist', 'Lyrics', 'Music', 'ISRC Code', 'Version Type', 'Ownership', 'Conflicts'];
        const rows = response.songs.map(s => [
          s.name,
          s.nameSinhala || '',
          s.artist,
          s.lyrics,
          s.music,
          s.isrcCode,
          s.versionType,
          `${s.ownership}%`,
          s.conflicts || s.conflict || 'No'
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        const formattedName = (album?.name || 'album').replace(/\s+/g, '_');
        link.setAttribute('download', `album_songs_${formattedName}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Album songs exported successfully!');
      } else {
        toast.error('Failed to export songs.');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export songs list.');
    } finally {
      setIsExporting(false);
    }
  };

  const albumLabelFallback = (album && (album.recordLabelName || album.labelName || album.record_label_name || album.name)) ? [{
    id: album.recordLabelId || album.record_label_id || album.id,
    name: album.recordLabelName || album.labelName || album.record_label_name || album.name,
    imageUrl: album.recordLabelImage || album.record_label_image || album.labelImage || album.imageUrl || album.image_url || null
  }] : [];

  const filteredSongs = songs.filter(s => 
    !searchQuery.trim() || 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (s.artist && s.artist.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="album-songs-tab-wrapper">
      <LoadingOverlay isLoading={isExporting} message={t('common.exportingMessage') || 'Exporting data...'} />
      <SharedHeader
        title=""
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('songs.searchPlaceholder') || 'Search songs...'}
        resultCount={totalCount || songs.length}
        resultLabel="Songs"
        showSortFilter={false}
        onExportClick={handleExportSongs}
      />
      <div 
        className="songs-tab-container"
        ref={tableContainerRef}
        onScroll={handleScroll}
        style={{ maxHeight: '600px', overflowY: 'auto', marginTop: '16px' }}
      >
      <table className="songs-tab-table">
        <thead>
          <tr>
            <th>{t('albumDetails.songsColSongName')}</th>
            <th>{t('albumDetails.songsColStatus')}</th>
            <th>{t('albumDetails.songsColArtist')}</th>
            <th>{t('albumDetails.songsColLyricsMusic')}</th>
            <th>{t('albumDetails.songsColLabels')}</th>
            <th>{t('albumDetails.songsColOwnership')}</th>
            <th>{t('albumDetails.songsColNotesCases')}</th>
            <th>{t('albumDetails.songsColConflicts')}</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="8" className="empty-state-td">
                <Loader label="Fetching songs, please wait." />
              </td>
            </tr>
          ) : filteredSongs.length === 0 ? (
            <tr>
              <td colSpan="8" className="empty-state-td">
                <EmptyState title="No Songs Found" subtitle="This album has no songs associated yet." />
              </td>
            </tr>
          ) : (
            <>
              {filteredSongs.map((song, index) => {
                const songLabels = (Array.isArray(song.labels) && song.labels.length > 0)
                  ? song.labels
                  : ((Array.isArray(song.recordLabels) && song.recordLabels.length > 0)
                      ? song.recordLabels
                      : albumLabelFallback);

                return (
                  <tr 
                    key={song.id || index} 
                    onClick={() => onSelectSong && onSelectSong(song)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="song-name-cell">
                        <span className="song-title-sinhala">{song.nameSinhala || song.name}</span>
                        <span className="song-title-english">{song.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="song-status-pill active">
                        <span className="status-dot"></span>
                        <span className="status-text">Active</span>
                      </div>
                    </td>
                    <td>
                      <div className="song-artists-cell">
                        <span className="artist-main">{song.artist || 'Unknown Artist'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="song-lyrics-music-cell">
                        <div className="lyrics-text" style={{ fontSize: '13px', fontWeight: '500', color: '#1e293b' }}>{song.lyrics || '—'}</div>
                        <div className="music-text" style={{ fontSize: '12px', color: '#64748b' }}>{song.music || '—'}</div>
                      </div>
                    </td>
                    <td>
                      <LabelAvatarStack labels={songLabels} />
                    </td>
                    <td>
                      <span className="ownership-text">{song.ownership !== undefined ? song.ownership : (song.ownershipPercentage !== undefined ? song.ownershipPercentage : 0)}%</span>
                    </td>
                    <td>
                      <span className="notes-cases-text">{song.notes || 'No Cases Or Notes'}</span>
                    </td>
                    <td>
                      <span className={`conflicts-badge ${song.conflictCount > 0 || (song.conflict && song.conflict !== 'No' && song.conflict !== '0') ? 'conflict' : 'no-conflict'}`}>
                        {song.conflicts || song.conflict || 'No'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {loadingMore && (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '16px', color: '#64748b', fontSize: '13px' }}>
                    Loading more songs...
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  </div>
);
}

export default SongsTab;
